import { usePackageProductItem } from '@jetshop/core/hooks/PackageProducts';
import Image from '@jetshop/ui/Image';
import React, { useState } from 'react';
import { styled } from 'linaria/react';
import StockStatusIndicator from '../StockStatus/StockStatusIndicator';
import { VariantSelector } from '../VariantSelector';
import { SizeGuideWrapper } from '../AddToCart/AddToCartForm';
import { PushPrice } from '../../ui/StyledPrice';
import { Paragraph } from '../../ui/text/Paragraph';
import { theme } from '../../Theme';
import { PopupContent } from '../../ui/PopupContent';
import { DynamicCategoryContentRenderer } from '../../ContentComponents/DynamicContentRenderer';
import { Above } from '@jetshop/ui/Breakpoints';

const PackageProductItemWrapper = styled('section')`
  border-bottom: 1px solid ${theme.colors.border};
  margin-bottom: 20px;
  padding-bottom: 20px;

  .size-guide-and-stockstatus {
    display: flex;
    justify-content: space-between;
    margin-top: 12px;
  }
`;

const ProductInfoRow = styled('div')`
  display: flex;
  width: 100%;
  margin-bottom: 20px;

  .image-wrapper {
    flex: 1;
    background: ${theme.colors.lightgrey};

    [data-flight-image-container] {
      mix-blend-mode: darken;
    }
  }

  .product-info {
    margin-left: 10px;
    flex: 2;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .top-row {
      p {
        margin-bottom: 2px;
      }
    }

    .bottom-row {
      display: flex;
      width: 100%;
      justify-content: end;

      [data-flight-price] {
        margin-bottom: 0;
        .price,
        .new-price {
          font-size: ${theme.fontSizes.largeParagraph};
          line-height: ${theme.fontSizes.largeParagraph};
          color: ${theme.colors.darkred};
          text-align: end;
        }

        .percentage,
        .old-price {
          font-size: ${theme.fontSizes.smallParagraph};
        }
      }
    }
  }
`;

export function PackageProductItem({
  item,
  updatePackageItem,
  validationEnabled,
  price
}) {
  const { stockStatus, variantHandler } = usePackageProductItem(
    item,
    updatePackageItem,
    { preselectCheapest: true }
  );
  const { selectedVariant } = variantHandler;
  const product = item.product;

  const [showSizeguide, setShowSizeguide] = useState(false);

  const sizeguide = product?.customFields?.find(
    customField => customField?.key === 'sizeguide'
  )?.stringValue;

  const imageData = selectedVariant ?? product;

  return (
    <PackageProductItemWrapper>
      <ProductInfoRow>
        <div className="image-wrapper">
          <Image
            sizes={[1 / 10]}
            aspect={'1:1'}
            alt={imageData?.images?.[0]?.alt}
            src={imageData?.images?.[0]?.url}
            modifiedDate={imageData?.images?.[0]?.modifiedDate}
          />
        </div>
        <div className="product-info">
          <div className="top-row">
            <Paragraph text={product?.subName} weight={'bold'} />
            <Paragraph text={product?.name} />
          </div>

          <div className="bottom-row">
            <PushPrice {...price} />
          </div>
        </div>
      </ProductInfoRow>
      {product?.hasVariants && (
        <VariantSelector
          product={product}
          variantHandler={variantHandler}
          showValidation={validationEnabled}
          disableOutOfStock
        />
      )}
      <div className="size-guide-and-stockstatus">
        {sizeguide?.length > 0 && (
          <SizeGuideWrapper
            onClick={() => {
              setShowSizeguide(true);
            }}
          >
            <Paragraph text={'Size guide'} />
          </SizeGuideWrapper>
        )}
        <StockStatusIndicator
          status={stockStatus?.status}
          text={stockStatus?.text}
        />
      </div>

      {showSizeguide && (
        <Above breakpoint="lg">
          {matches => (
            <PopupContent
              type={matches ? null : 'curtain'}
              open={showSizeguide}
              setOpen={setShowSizeguide}
            >
              <DynamicCategoryContentRenderer
                categoryId={sizeguide ?? 284} // TODO - remove default sizeguide id
              />
            </PopupContent>
          )}
        </Above>
      )}
    </PackageProductItemWrapper>
  );
}
